/**
 * Chat-with-AI panel. Slides over from the right when toggled, overlays
 * the preview iframe. The operator chats with DeepSeek about the article;
 * suggestions appear with a copy button so the operator can paste into
 * the editor manually.
 *
 *   - GET /api/sites/:siteId/drafts/:slug/chat loads history on mount
 *   - POST streams the assistant reply via SSE; we render deltas live
 *   - DELETE clears the chat thread (start fresh)
 *   - Slash command: /corpus <topic> on the start of a line injects top-3
 *     matching corpus items into THAT turn's context (handled server-side)
 */

import { useEffect, useRef, useState } from 'react'
import { api, API_BASE } from '../lib/api.ts'
import { getToken } from '../lib/auth.ts'
import { friendlyError } from '../lib/friendly-errors.ts'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  at: number
  cost_cents?: number | null
}

interface Props {
  siteId: string
  slug: string
  open: boolean
  onClose: () => void
}

export function ChatPanel({ siteId, slug, open, onClose }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  // Load history on open.
  useEffect(() => {
    if (!open) return
    api<{ messages: ChatMessage[] }>(`/api/sites/${siteId}/drafts/${slug}/chat`)
      .then((res) => setMessages(res.messages))
      .catch((err) => setError(friendlyError(err).message))
  }, [open, siteId, slug])

  // Auto-scroll to bottom when messages or streaming text change.
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, streamingText])

  async function send() {
    const content = input.trim()
    if (!content || streaming) return
    setInput('')
    setError(null)

    // Optimistic add of user message
    const tempUser: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      at: Math.floor(Date.now() / 1000),
    }
    setMessages((prev) => [...prev, tempUser])
    setStreaming(true)
    setStreamingText('')

    try {
      // POST + read SSE
      const token = getToken()
      const res = await fetch(`${API_BASE}/api/sites/${siteId}/drafts/${slug}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content }),
      })
      if (!res.ok || !res.body) {
        const errBody = await res.text().catch(() => '')
        throw new Error(`${res.status}: ${errBody.slice(0, 200)}`)
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      let full = ''
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        let idx
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, idx)
          buf = buf.slice(idx + 2)
          let evt = 'message'
          let data = ''
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) evt = line.slice(6).trim()
            else if (line.startsWith('data:')) data += line.slice(5).trim()
          }
          if (!data) continue
          if (evt === 'delta') {
            try {
              const { delta } = JSON.parse(data) as { delta: string }
              full += delta
              setStreamingText(full)
            } catch {
              /* skip */
            }
          } else if (evt === 'done') {
            // Reload from server to get the persisted message id + cost
            const fresh = await api<{ messages: ChatMessage[] }>(
              `/api/sites/${siteId}/drafts/${slug}/chat`,
            )
            setMessages(fresh.messages)
            setStreamingText('')
            setStreaming(false)
            return
          } else if (evt === 'error') {
            try {
              const { message } = JSON.parse(data) as { message: string }
              setError(message)
            } catch {
              setError('Stream error')
            }
            setStreaming(false)
            return
          }
        }
      }
    } catch (err) {
      setError((err as Error).message)
      setStreaming(false)
    }
  }

  async function clearChat() {
    if (!confirm('Clear all chat history for this article?')) return
    try {
      await api(`/api/sites/${siteId}/drafts/${slug}/chat`, { method: 'DELETE' })
      setMessages([])
    } catch (err) {
      alert(friendlyError(err).message)
    }
  }

  if (!open) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(540px, 50vw)',
        background: 'var(--bg-elev)',
        borderLeft: '1px solid var(--border-strong)',
        boxShadow: '-10px 0 30px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 50,
      }}
    >
      {/* header */}
      <div
        style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>💬 Chat about this article</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>
            DeepSeek has your article + standards. Slash commands: <em>/corpus &lt;topic&gt;</em>, <em>/links</em>.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn"
            style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
            onClick={clearChat}
            disabled={messages.length === 0}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn"
            style={{ fontSize: '0.85rem', padding: '0.3rem 0.65rem' }}
            onClick={onClose}
          >
            ×
          </button>
        </div>
      </div>

      {/* messages */}
      <div ref={scrollerRef} style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {messages.length === 0 && !streaming && (
          <div style={{ color: 'var(--text-faint)', fontSize: '0.85rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>Try one of these:</div>
            <ul style={{ paddingLeft: '1rem', margin: 0 }}>
              {SUGGESTIONS.map((s, i) => (
                <li
                  key={i}
                  style={{ padding: '0.2rem 0', cursor: 'pointer', color: 'var(--accent)' }}
                  onClick={() => setInput(s)}
                >
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {streaming && streamingText && (
          <MessageBubble
            message={{
              id: 'streaming',
              role: 'assistant',
              content: streamingText,
              at: Math.floor(Date.now() / 1000),
            }}
            streaming
          />
        )}
        {error && (
          <div
            style={{
              color: 'var(--danger)',
              background: '#fef2f2',
              padding: '0.5rem 0.75rem',
              borderRadius: 'var(--radius)',
              fontSize: '0.8rem',
              marginTop: '0.5rem',
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* input */}
      <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border)' }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !streaming) {
              e.preventDefault()
              send()
            }
          }}
          placeholder="Ask anything about this article. Enter to send · Shift+Enter for newline."
          rows={3}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius)',
            fontFamily: 'inherit',
            fontSize: '0.85rem',
            resize: 'vertical',
            minHeight: '4rem',
          }}
          disabled={streaming}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.4rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={streaming || input.trim().length === 0}
            onClick={send}
          >
            {streaming ? 'Streaming…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}

const SUGGESTIONS = [
  'Give me 5 alternate H1s — keep the keyword, more VOC-aligned',
  'Rewrite s3.body in a more confessional tone',
  'What chart would best show the 90% positive-attitude stat?',
  'Suggest a stronger hook for hero.dek',
  'List 3 H2 alternatives for s5 — make it more action-driven',
  '/links Suggest 3-4 internal links I can drop into the body',
]

function MessageBubble({ message, streaming = false }: { message: ChatMessage; streaming?: boolean }) {
  const isUser = message.role === 'user'
  const [copied, setCopied] = useState(false)

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  return (
    <div
      style={{
        marginBottom: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <div
        style={{
          maxWidth: '92%',
          padding: '0.55rem 0.8rem',
          borderRadius: 'var(--radius-lg)',
          background: isUser ? 'var(--accent-soft)' : 'var(--bg-muted)',
          color: 'var(--text)',
          fontSize: '0.85rem',
          lineHeight: 1.45,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: isUser ? 'inherit' : 'inherit',
        }}
      >
        {message.content}
        {streaming && <span style={{ opacity: 0.5, marginLeft: '0.25rem' }}>▍</span>}
      </div>
      {!isUser && !streaming && (
        <button
          type="button"
          onClick={copyAll}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--text-faint)',
            fontSize: '0.7rem',
            cursor: 'pointer',
            padding: '0.2rem 0',
            marginTop: '0.15rem',
          }}
        >
          {copied ? '✓ copied' : '📋 copy'}
          {message.cost_cents != null && message.cost_cents > 0 && (
            <span style={{ marginLeft: '0.5rem' }}>· {(message.cost_cents / 100).toFixed(3)}¢</span>
          )}
        </button>
      )}
    </div>
  )
}
